const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_RANGE_DAYS = 366;

export const PLACEHOLDER_NOTE =
  'Leave {team}, {player}, and {rival} in the copy. They are filled when a player picks a team or player.';

const LAYOUT_LABELS = {
  spectrum: 'Spectrum',
  multi_choice: 'Multiple choice',
  reaction_grid: 'Reaction grid',
  single_choice: 'Single choice',
  pick_a_side: 'Pick a side',
};

const LAYOUT_HELP = {
  spectrum: 'Left and right labels sit under the bar. Each option’s text is the bubble for that step.',
  single_choice: 'Option text is the row the player taps.',
  pick_a_side: 'Players pick the left or right side of every pair.',
};

export const FAN_SCAN_QUESTION_IDS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10', 'q11'];

const MAX_IMAGE_URL_LENGTH = 2048;
export const FAN_SCAN_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const FAN_SCAN_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

/** Empty when the file can be sent to POST /admin/fan-scan/upload-image. */
export function fanScanUploadFileError(file) {
  if (!file) return 'Choose an image file.';
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  const allowedName = /\.(png|jpe?g|webp|gif)$/.test(name);
  if (!FAN_SCAN_IMAGE_TYPES.has(type) && !(type === '' && allowedName)) {
    return 'Upload a PNG, JPEG, WEBP, or GIF.';
  }
  if (Number(file.size) > FAN_SCAN_IMAGE_MAX_BYTES) return 'Image has to be 8MB or smaller.';
  return '';
}

export function layoutLabel(kind) {
  return LAYOUT_LABELS[kind] || kind || 'Question';
}

export function layoutHelp(kind, question) {
  if (kind === 'reaction_grid') {
    return 'Each option is a sticker. The app shows the image. Analytics uses the text, then the label, then the option id.';
  }
  if (kind === 'multi_choice') {
    const max = Number(question?.max_select);
    if (Number.isFinite(max) && max > 0) {
      return `Option text is the row. Players may pick up to ${max}.`;
    }
    return 'Option text is the row. Players may pick more than one.';
  }
  return LAYOUT_HELP[kind] || '';
}

/** The test API accepts http. Production accepts https only. */
export function allowsHttpImageUrls() {
  try {
    return localStorage.getItem('env') !== 'true';
  } catch {
    return true;
  }
}

export function imageUrlError(value, { allowHttp = false, subject = 'Background image' } = {}) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (text.length > MAX_IMAGE_URL_LENGTH) {
    return `${subject} has to be ${MAX_IMAGE_URL_LENGTH} characters or fewer.`;
  }
  let url;
  try {
    url = new URL(text);
  } catch {
    return `${subject} has to be an https URL.`;
  }
  if (url.protocol === 'https:') return '';
  if (url.protocol === 'http:' && allowHttp) return '';
  if (url.protocol === 'http:') return `${subject} has to be https in production.`;
  return `${subject} has to be an https URL.`;
}

export const RESULT_TITLE_MAX = 120;
export const RESULT_DESCRIPTION_MAX = 2000;

/** Chart label. The title is the headline. The emoji is not edited here. */
export function identityTitle(identity) {
  return identity?.title || identity?.name || identity?.id || '';
}

export function resultCategoriesFromPayload(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.categories)) return data.categories;
  if (Array.isArray(data.result_categories)) return data.result_categories;
  return [];
}

export function resultCategoryFromPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (data.category?.id) return data.category;
  if (data.result_category?.id) return data.result_category;
  if (data.id && (data.title != null || data.description != null)) return data;
  return null;
}

export function toResultCategoryDraft(category) {
  if (!category) return null;
  return {
    title: category.title ?? '',
    description: category.description ?? '',
    image_url: category.image_url ?? '',
  };
}

export function collectResultCategoryErrors(draft, { allowHttp = false } = {}) {
  const errors = [];
  const title = String(draft?.title ?? '').trim();
  const description = String(draft?.description ?? '').trim();
  if (!title) errors.push('Title is required.');
  else if (title.length > RESULT_TITLE_MAX) {
    errors.push(`Title has to be ${RESULT_TITLE_MAX} characters or fewer.`);
  }
  if (!description) errors.push('Description is required.');
  else if (description.length > RESULT_DESCRIPTION_MAX) {
    errors.push(`Description has to be ${RESULT_DESCRIPTION_MAX} characters or fewer.`);
  }
  const imageError = imageUrlError(draft?.image_url, { allowHttp, subject: 'Image' });
  if (imageError) errors.push(imageError);
  return errors;
}

/** Only the fields that differ. Null image clears it. */
export function buildResultCategorySaveBody(category, draft) {
  if (!category || !draft) return null;
  const body = {};
  const title = String(draft.title ?? '').trim();
  const description = String(draft.description ?? '').trim();
  if (title !== String(category.title ?? '').trim()) body.title = title;
  if (description !== String(category.description ?? '').trim()) body.description = description;
  const beforeImage = String(category.image_url ?? '').trim();
  const nextImage = String(draft.image_url ?? '').trim();
  if (nextImage !== beforeImage) body.image_url = nextImage === '' ? null : nextImage;
  return Object.keys(body).length ? body : null;
}

export function validateQuestionOrder(ids) {
  if (!Array.isArray(ids) || ids.length !== FAN_SCAN_QUESTION_IDS.length) {
    return 'The order has to list each question from q1 through q11 once.';
  }
  const seen = new Set();
  for (const id of ids) {
    if (!FAN_SCAN_QUESTION_IDS.includes(id)) return `Unknown question "${id}".`;
    if (seen.has(id)) return `Question ${id} is listed twice.`;
    seen.add(id);
  }
  return '';
}

export function formatCount(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return number.toLocaleString('en-US');
}

export function formatRate(rate) {
  const value = Number(rate);
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

export function formatUtcStamp(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

export function parseUtcDay(value) {
  if (!DATE_PATTERN.test(String(value || ''))) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Empty from and to means the server’s last 30 days. Both must be set together. */
export function validateDateRange(from, to) {
  const startText = String(from || '').trim();
  const endText = String(to || '').trim();
  if (!startText && !endText) return '';
  if (!startText || !endText) {
    return 'Set both dates, or clear both for the last 30 days.';
  }
  const start = parseUtcDay(startText);
  const end = parseUtcDay(endText);
  if (!start || !end) return 'Use dates as YYYY-MM-DD.';
  if (start > end) return 'From has to be on or before to.';
  const days = Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
  if (days > MAX_RANGE_DAYS) return 'The range cannot be longer than 366 days.';
  return '';
}

export function funnelWithDrop(funnel, started) {
  let previous = Number(started) || 0;
  return (funnel || []).map((row, index) => {
    const answered = Number(row.answered) || 0;
    const drop = Math.max(0, previous - answered);
    previous = answered;
    return {
      ...row,
      answered,
      dropFromPrevious: drop,
      comparedWith: index === 0 ? 'started' : 'previous',
    };
  });
}

export function shortText(text, max = 72) {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function weightDraft(weights) {
  const draft = {};
  Object.entries(weights || {}).forEach(([key, value]) => {
    const number = Number(value);
    if (Number.isFinite(number) && number !== 0) draft[key] = String(value);
  });
  return draft;
}

function imageDraft(value) {
  return value ?? '';
}

function sideDraft(side) {
  return {
    id: side?.id || '',
    text: side?.text ?? '',
    weights: weightDraft(side?.weights),
  };
}

export function toQuestionDraft(question) {
  if (!question) return null;
  return {
    title: question.title ?? '',
    context: question.context ?? '',
    prompt: question.prompt ?? '',
    left_label: question.left_label ?? '',
    right_label: question.right_label ?? '',
    background_image_url: imageDraft(question.background_image_url),
    background_color: question.background_color ?? '',
    aspect_ratio: question.aspect_ratio ?? '',
    presentation: question.presentation ?? '',
    options: (question.options || []).map((option) => ({
      id: option.id,
      label: option.label ?? '',
      text: option.text ?? '',
      position: option.position ?? null,
      sticker_image_url: imageDraft(option.sticker_image_url),
      weights: weightDraft(option.weights),
    })),
    pairs: (question.pairs || []).map((pair) => ({
      id: pair.id,
      label: pair.label ?? '',
      left: sideDraft(pair.left),
      right: sideDraft(pair.right),
    })),
  };
}

export function parseWeight(raw) {
  if (raw == null || String(raw).trim() === '') return 0;
  const text = String(raw).trim();
  if (!/^-?\d+$/.test(text)) return NaN;
  return Number(text);
}

function parsedWeightMap(weights) {
  const map = {};
  Object.entries(weights || {}).forEach(([key, raw]) => {
    map[key] = parseWeight(raw);
  });
  return map;
}

export function validateWeights(weights, { traitKeys = null, subject = 'This option' } = {}) {
  const errors = [];
  const known = traitKeys ? new Set(traitKeys) : null;

  Object.entries(weights || {}).forEach(([key, raw]) => {
    if (raw == null || String(raw).trim() === '') return;
    if (known && !known.has(key)) {
      errors.push(`${subject}: unknown trait "${key}".`);
      return;
    }
    const value = typeof raw === 'number' ? raw : parseWeight(raw);
    if (Number.isNaN(value) || !Number.isInteger(value) || value < 1 || value > 10) {
      errors.push(`${subject}: ${key} has to be a whole number from 1 to 10.`);
    }
  });

  return errors;
}

function labelOrNull(value) {
  if (value == null || String(value).trim() === '') return null;
  return String(value);
}

function isHexColor(value) {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

function isGradient(value) {
  return /^linear-gradient\([\s\S]+\)$/i.test(value);
}

const COLON_RATIOS = ['1:1', '5:4', '4:3', '3:4', '4:5', '16:9', '9:16', '3:2', '2:3'];

const FEED_PRESETS = [
  ['1:1', 1],
  ['5:4', 5 / 4],
  ['4:3', 4 / 3],
  ['3:4', 3 / 4],
  ['4:5', 4 / 5],
  ['16:9', 16 / 9],
  ['9:16', 9 / 16],
];

export const PRESENTATIONS = ['backdrop', 'bleed', 'hero'];

/** Colon pair, or '' when blank. Null when the value cannot be stored. */
export function canonicalAspectRatio(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  if (COLON_RATIOS.includes(text)) return text;
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const number = Number(text);
  if (!Number.isFinite(number) || number <= 0) return null;
  let best = null;
  let bestGap = Infinity;
  FEED_PRESETS.forEach(([label, value]) => {
    const gap = Math.abs(number - value) / value;
    if (gap < bestGap) {
      bestGap = gap;
      best = label;
    }
  });
  if (best != null && bestGap <= 0.02) return best;
  return null;
}

export function aspectRatioError(value, { hasImage = true } = {}) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (!hasImage) return 'Aspect ratio needs a background image.';
  if (canonicalAspectRatio(text)) return '';
  return 'Aspect ratio is a W:H pair such as 16:9, 9:16, 1:1, 4:3, 3:4, 3:2, or 2:3. A decimal within about 2% of a feed preset is saved as that pair.';
}

export function presentationError(value, { hasImage = true } = {}) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (!hasImage) return 'Presentation needs a background image.';
  if (PRESENTATIONS.includes(text)) return '';
  return 'Presentation is backdrop, bleed, or hero.';
}

export function backgroundColorError(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (isHexColor(text) || isGradient(text)) return '';
  return 'Background color is # plus 6 hex digits, or a linear-gradient(...).';
}

/** Hex is stored uppercase. A gradient is stored as typed. Null clears it. */
function colorForSave(original, draft) {
  const beforeRaw = String(original ?? '').trim();
  const next = String(draft ?? '').trim();
  if (!next) return beforeRaw ? null : undefined;
  if (isHexColor(next)) {
    const upper = next.toUpperCase();
    const before = isHexColor(beforeRaw) ? beforeRaw.toUpperCase() : beforeRaw;
    if (upper === before) return undefined;
    return upper;
  }
  if (isGradient(next)) {
    if (next === beforeRaw) return undefined;
    return next;
  }
  return undefined;
}

function mediaTextForSave(original, draft, canonicalize) {
  const nextRaw = String(draft ?? '').trim();
  if (!nextRaw) return undefined;
  const next = canonicalize ? canonicalize(nextRaw) : nextRaw;
  if (!next) return undefined;
  const before = String(original ?? '').trim();
  if (next === before) return undefined;
  return next;
}

/** Undefined when the image is unchanged. Null clears it. */
function imageForSave(original, draft) {
  const before = String(original ?? '').trim();
  const next = String(draft ?? '').trim();
  if (next === before) return undefined;
  return next === '' ? null : next;
}

function weightsChanged(original, next) {
  const before = original || {};
  const keys = new Set([...Object.keys(before), ...Object.keys(next || {})]);
  for (const key of keys) {
    const left = Number(before[key] || 0);
    const right = Number(next[key] || 0);
    if (left !== right) return true;
  }
  return false;
}

/** Full desired map when anything changed. A missing trait is left off. Never send 0. {} clears every trait. */
function weightsForSave(original, next) {
  if (!weightsChanged(original, next)) return undefined;
  const payload = {};
  Object.entries(next || {}).forEach(([key, value]) => {
    const number = Number(value);
    if (Number.isInteger(number) && number >= 1 && number <= 10) payload[key] = number;
  });
  return payload;
}

function buildOptionsPayload(originalOptions, draftOptions, { stickers = false } = {}) {
  const payload = [];
  (originalOptions || []).forEach((original) => {
    const draft = (draftOptions || []).find((option) => option.id === original.id);
    if (!draft) return;
    const row = { id: original.id };
    let changed = false;
    if ((draft.text ?? '') !== (original.text ?? '')) {
      row.text = draft.text ?? '';
      changed = true;
    }
    if (labelOrNull(draft.label) !== labelOrNull(original.label)) {
      row.label = labelOrNull(draft.label);
      changed = true;
    }
    const weights = weightsForSave(original.weights, parsedWeightMap(draft.weights));
    if (weights !== undefined) {
      row.weights = weights;
      changed = true;
    }
    if (stickers) {
      const sticker = imageForSave(original.sticker_image_url, draft.sticker_image_url);
      if (sticker !== undefined) {
        row.sticker_image_url = sticker;
        changed = true;
      }
    }
    if (changed) payload.push(row);
  });
  return payload.length ? payload : null;
}

function buildSidePayload(original, draft) {
  if (!original?.id) return null;
  const row = { id: original.id };
  let changed = false;
  if ((draft?.text ?? '') !== (original.text ?? '')) {
    row.text = draft?.text ?? '';
    changed = true;
  }
  const weights = weightsForSave(original.weights, parsedWeightMap(draft?.weights));
  if (weights !== undefined) {
    row.weights = weights;
    changed = true;
  }
  return changed ? row : null;
}

function buildPairsPayload(originalPairs, draftPairs) {
  const payload = [];
  (originalPairs || []).forEach((original) => {
    const draft = (draftPairs || []).find((pair) => pair.id === original.id);
    if (!draft) return;
    const row = { id: original.id };
    let changed = false;
    if ((draft.label ?? '') !== (original.label ?? '')) {
      row.label = draft.label ?? '';
      changed = true;
    }
    const left = buildSidePayload(original.left, draft.left);
    const right = buildSidePayload(original.right, draft.right);
    if (left) {
      row.left = left;
      changed = true;
    }
    if (right) {
      row.right = right;
      changed = true;
    }
    if (changed) payload.push(row);
  });
  return payload.length ? payload : null;
}

export function collectQuestionErrors(question, draft, traits, { allowHttp = false } = {}) {
  if (!question || !draft) return ['This question is still loading.'];
  const errors = [];
  const traitKeys = (traits || []).map((trait) => trait.key);
  const knownTraits = traitKeys.length ? traitKeys : null;
  const preference = question.layout_kind === 'pick_a_side';
  const stickers = question.layout_kind === 'reaction_grid';
  const hasImage = Boolean(String(draft.background_image_url || '').trim());
  const questionImageError = imageUrlError(draft.background_image_url, {
    allowHttp,
    subject: 'Question background image',
  });
  if (questionImageError) errors.push(questionImageError);
  const colorError = backgroundColorError(draft.background_color);
  if (colorError) errors.push(colorError);
  const ratioError = aspectRatioError(draft.aspect_ratio, { hasImage });
  if (ratioError) errors.push(ratioError);
  if (hasImage && String(question.aspect_ratio || '').trim() && !String(draft.aspect_ratio || '').trim()) {
    errors.push('Clear the background image to remove the aspect ratio.');
  }
  const frameError = presentationError(draft.presentation, { hasImage });
  if (frameError) errors.push(frameError);
  if (hasImage && String(question.presentation || '').trim() && !String(draft.presentation || '').trim()) {
    errors.push('Clear the background image to remove the presentation.');
  }

  if (!String(draft.title || '').trim()) errors.push('Title is required.');
  if (!String(draft.prompt || '').trim()) errors.push('Prompt is required.');
  if (question.layout_kind === 'spectrum') {
    if (!String(draft.left_label || '').trim()) errors.push('The left label is required.');
    if (!String(draft.right_label || '').trim()) errors.push('The right label is required.');
  }

  if (preference) {
    (question.pairs || []).forEach((pair) => {
      const draftPair = (draft.pairs || []).find((item) => item.id === pair.id);
      if (!draftPair) {
        errors.push(`Pair ${pair.id} is missing.`);
        return;
      }
      if (!String(draftPair.label || '').trim()) errors.push(`${pair.id}: the pair label is required.`);
      ['left', 'right'].forEach((sideName) => {
        const side = pair[sideName];
        const draftSide = draftPair[sideName] || {};
        if (!String(draftSide.text || '').trim()) {
          errors.push(`${side?.id || pair.id}: side text is required.`);
        }
        errors.push(
          ...validateWeights(draftSide.weights, {
            traitKeys: knownTraits,
            subject: side?.id || `${pair.id} ${sideName}`,
          })
        );
      });
    });
    return errors;
  }

  (question.options || []).forEach((option) => {
    const draftOption = (draft.options || []).find((item) => item.id === option.id);
    if (!draftOption) {
      errors.push(`Option ${option.id} is missing.`);
      return;
    }
    if (!stickers && !String(draftOption.text || '').trim()) {
      errors.push(`${option.id}: option text is required.`);
    }
    if (stickers) {
      const stickerError = imageUrlError(draftOption.sticker_image_url, {
        allowHttp,
        subject: `${option.id} sticker`,
      });
      if (stickerError) errors.push(stickerError);
    }
    errors.push(
      ...validateWeights(draftOption.weights, {
        traitKeys: knownTraits,
        subject: option.id,
      })
    );
  });
  return errors;
}

/** Fields that differ from the live question. Null when nothing editable changed. */
export function buildQuestionSaveBody(question, draft) {
  if (!question || !draft) return null;
  const body = {};
  if ((draft.title ?? '') !== (question.title ?? '')) body.title = draft.title ?? '';
  if ((draft.context ?? '') !== (question.context ?? '')) {
    body.context = draft.context === '' ? null : draft.context;
  }
  if ((draft.prompt ?? '') !== (question.prompt ?? '')) body.prompt = draft.prompt ?? '';
  const background = imageForSave(question.background_image_url, draft.background_image_url);
  if (background === null) {
    body.background_image_url = null;
    if (String(question.aspect_ratio || '').trim() || String(draft.aspect_ratio || '').trim()) {
      body.aspect_ratio = null;
    }
    if (String(question.presentation || '').trim() || String(draft.presentation || '').trim()) {
      body.presentation = null;
    }
  } else {
    if (background !== undefined) body.background_image_url = background;
    const ratio = mediaTextForSave(question.aspect_ratio, draft.aspect_ratio, canonicalAspectRatio);
    if (ratio !== undefined) body.aspect_ratio = ratio;
    const presentation = mediaTextForSave(question.presentation, draft.presentation, (value) =>
      PRESENTATIONS.includes(value) ? value : ''
    );
    if (presentation !== undefined) body.presentation = presentation;
  }
  const color = colorForSave(question.background_color, draft.background_color);
  if (color !== undefined) body.background_color = color;

  if (question.layout_kind === 'spectrum') {
    if ((draft.left_label ?? '') !== (question.left_label ?? '')) body.left_label = draft.left_label ?? '';
    if ((draft.right_label ?? '') !== (question.right_label ?? '')) body.right_label = draft.right_label ?? '';
  }

  if (question.layout_kind === 'pick_a_side') {
    const pairs = buildPairsPayload(question.pairs, draft.pairs);
    if (pairs) body.pairs = pairs;
  } else {
    const options = buildOptionsPayload(question.options, draft.options, {
      stickers: question.layout_kind === 'reaction_grid',
    });
    if (options) body.options = options;
  }

  return Object.keys(body).length ? body : null;
}

export function bundleIsEditable(status) {
  return !status || status === 'draft';
}

/** Chart and list label: answer text, then the option label, then the option id. */
export function choiceDisplayText(choice) {
  const text = String(choice?.text || '').trim();
  if (text) return text;
  const label = String(choice?.label || '').trim();
  if (label) return label;
  return String(choice?.id || '');
}

export const SEO_TITLE_MAX = 120;
export const SEO_DESCRIPTION_MAX = 320;

const CANONICAL_PATH = /^\/[A-Za-z0-9/_-]*$/;

export function seoFromPayload(data) {
  return data?.seo || null;
}

export function toSeoDraft(seo) {
  return {
    title: seo?.title ?? '',
    description: seo?.description ?? '',
    canonical_path: seo?.canonical_path ?? '',
  };
}

export function canonicalPathError(value) {
  const text = String(value ?? '').trim();
  if (!text) return 'Canonical path is required.';
  if (text.includes('://') || text.includes('?') || text.includes('#')) {
    return 'Canonical path is a path on this site, not a full URL.';
  }
  if (!CANONICAL_PATH.test(text)) {
    return 'Canonical path starts with / and uses only letters, numbers, /, _, and -.';
  }
  return '';
}

export function collectSeoErrors(draft) {
  const errors = [];
  const title = String(draft?.title ?? '').trim();
  const description = String(draft?.description ?? '').trim();
  if (!title) errors.push('Title is required.');
  else if (title.length > SEO_TITLE_MAX) errors.push(`Title has to be ${SEO_TITLE_MAX} characters or fewer.`);
  if (!description) errors.push('Description is required.');
  else if (description.length > SEO_DESCRIPTION_MAX) {
    errors.push(`Description has to be ${SEO_DESCRIPTION_MAX} characters or fewer.`);
  }
  const pathError = canonicalPathError(draft?.canonical_path);
  if (pathError) errors.push(pathError);
  return errors;
}

/** Changed SEO fields only. Empty strings are rejected before this runs. */
export function buildSeoSaveBody(seo, draft) {
  if (!seo || !draft) return null;
  const body = {};
  const title = String(draft.title ?? '').trim();
  const description = String(draft.description ?? '').trim();
  const path = String(draft.canonical_path ?? '').trim();
  if (title !== String(seo.title ?? '').trim()) body.title = title;
  if (description !== String(seo.description ?? '').trim()) body.description = description;
  if (path !== String(seo.canonical_path ?? '').trim()) body.canonical_path = path;
  return Object.keys(body).length ? body : null;
}
